/**
 * <AppLayout>
 *
 * The top-level shell for every authenticated route. Owns:
 *   1. The two-column (sidebar + main) desktop layout.
 *   2. The single-column mobile layout with a fixed bottom nav.
 *   3. The scrollable main pane (so the sidebar can stay fixed while content
 *      scrolls independently — no full-page scroll for content).
 *   4. The <AnimatePresence> that drives page-transition motion between
 *      route changes.
 *
 * ─── Layout rules that this component enforces ─────────────────────────────
 *
 * • The outer wrapper is a CSS grid, NOT flex. Grid gives us guaranteed track
 *   widths that don't collapse — one of the previous iteration's failures was
 *   using flex + `flex-1` on the main column, which caused the content pane
 *   to swallow the sidebar when children hit `min-width: min-content`.
 *
 * • Grid template columns:
 *     Mobile  →  1fr           (single column; sidebar hidden)
 *     Desktop →  16rem 1fr     (fixed sidebar + fluid main)
 *
 * • The main column is itself a flex-col so the sticky TopBar can sit above
 *   a scrollable <main>. Only the <main> element scrolls — the surrounding
 *   grid and TopBar stay put.
 *
 * • `min-h-0` on the main column is critical: without it, the flex column
 *   would grow past the viewport when children are tall, breaking the
 *   internal-scroll model.
 *
 * • No absolute positioning is used for structural elements. Everything is
 *   in normal flow (grid → flex → grid) so the browser can measure and
 *   reflow cleanly. Absolute positioning is reserved for overlays (modals,
 *   tooltips, the fixed BottomNav on mobile).
 *
 * ─── Animation rules ──────────────────────────────────────────────────────
 *
 * • AnimatePresence with `mode="wait"` ensures the exiting page finishes
 *   before the entering page arrives. This avoids the "two pages briefly
 *   overlapping" flash.
 *
 * • The keyed motion.div uses `location.pathname` — every navigation causes
 *   a real remount, which is exactly what AnimatePresence needs to detect.
 *
 * • The individual pages don't need to know about this; they just render
 *   their content. If a page wants extra polish it can compose <FadeIn>
 *   inside itself.
 */

import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { SideNav } from './SideNav';
import { BottomNav } from './BottomNav';
import { TopBar } from './TopBar';
import { pageVariants } from '@/components/common/motion-variants';

export function AppLayout() {
  const location = useLocation();
  const shouldReduce = useReducedMotion();

  return (
    <div
      className={[
        // ── Grid template ────────────────────────────────────────────────
        // Mobile: one column. Desktop (md+): 16rem sidebar + 1fr main.
        'grid grid-cols-1 md:grid-cols-[16rem_1fr]',
        // ── Full viewport height ────────────────────────────────────────
        // `h-dvh` = dynamic viewport height. Handles iOS toolbar collapse
        // better than 100vh. h-screen is the fallback.
        'h-dvh h-screen',
        // ── Prevent whole-page scroll ───────────────────────────────────
        // Only <main> scrolls. This keeps the sidebar and topbar fixed in
        // place even when content is long — a big UX win on desktop.
        'overflow-hidden',
        // Calm & Soothing light palette (dark variant flips at .dark).
        'bg-mist-50 text-earth-900 dark:bg-earth-950 dark:text-mist-50',
      ].join(' ')}
    >
      {/*
        SIDEBAR — desktop only.
        Hidden below md via the component itself. Because it lives in a grid
        track, it never overlaps main content and never collapses.
      */}
      <SideNav />

      {/*
        MAIN COLUMN — TopBar + scrollable content.
        `min-w-0` prevents grid children from expanding based on intrinsic
        content width (important for text wrapping in the main pane).
        `min-h-0` allows the flex layout inside to constrain <main>'s height
        so it can scroll internally instead of pushing the layout down.
      */}
      <div className="flex min-h-0 min-w-0 flex-col">
        <TopBar />

        <main
          id="main"
          role="main"
          tabIndex={-1}
          className={[
            // Fill the remaining vertical space of the flex column.
            'flex-1',
            // Own the scroll — internal scrolling instead of page scroll.
            'overflow-y-auto',
            // Smooth momentum scrolling on iOS.
            'overscroll-contain [-webkit-overflow-scrolling:touch]',
            // Bottom padding on mobile to clear the fixed BottomNav +
            // iOS home indicator. On desktop this collapses to 0.
            'pb-[calc(7.5rem+env(safe-area-inset-bottom))] md:pb-0',
          ].join(' ')}
        >
          {/*
            ROUTE-TRANSITION LAYER
            AnimatePresence in "wait" mode: previous page exits, then the
            next page enters. `initial={false}` skips the entrance animation
            on the first mount (hard reload) so the app doesn't feel sluggish
            on cold load.
          */}
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={shouldReduce ? false : 'initial'}
              animate="animate"
              variants={pageVariants}
              className="min-h-full will-change-[transform,opacity]"
              {...(shouldReduce ? {} : { exit: 'exit' })}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </main>
      </div>

      {/*
        MOBILE BOTTOM NAV — fixed at the bottom, hidden ≥md.
        Uses `position: fixed`, which is fine because it's an overlay, not a
        structural layout element. Sits above content via a modest z-index.
      */}
      <BottomNav />
    </div>
  );
}
